import { useState } from "react";
import Button from "antd/es/button";
import Modal from "antd/es/modal";
import DatePicker from "antd/es/date-picker";
import Input from "antd/es/input";
import message from "antd/es/message";
import { useForm, Controller } from "react-hook-form";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import plPL from "antd/es/date-picker/locale/pl_PL";
import api from "./api";
import { logOut } from "./Dashboard";

dayjs.locale("pl");

const { REACT_APP_API_URL: API_URL } = process.env;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OwnerBookingModal({
  open,
  onClose,
  onSuccess,
  bookings,
  selectedRental,
  contractEnd,
}) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  function handleClose() {
    reset();
    onClose();
  }

  function isDateOverlapping(checkIn, checkOut) {
    return bookings.some((b) => {
      return (
        dayjs(checkIn).isBefore(dayjs(b.until_date)) &&
        dayjs(checkOut).isAfter(dayjs(b.from_date))
      );
    });
  }

  async function handleFormSubmit(data) {
    const [checkInDate, checkOutDate] = data.date_range;
    const checkIn = checkInDate.format("YYYY-MM-DD");
    const checkOut = checkOutDate.format("YYYY-MM-DD");

    if (isDateOverlapping(checkIn, checkOut)) {
      message.error(intl.formatMessage({ id: "owner-booking-overlap" }));
      return;
    }

    setLoading(true);
    try {
      await api({
        method: "post",
        url: `${API_URL}/costs/owner-booking`,
        headers: { Authorization: `Bearer ${localStorage.token}` },
        data: {
          rental_id: selectedRental,
          check_in: checkIn,
          check_out: checkOut,
          guests_count: data.guests_count,
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
          email: data.email,
        },
      });
      message.success(intl.formatMessage({ id: "owner-booking-success" }));
      reset();
      onSuccess();
    } catch (error) {
      if (error.response?.status === 401) {
        logOut(intl);
      } else if (error.response?.status === 403) {
        const errorCode = error.response?.data?.error;
        if (errorCode === "OWNER_BOOKING_NOT_ENABLED") {
          message.error(
            intl.formatMessage({ id: "owner-booking-not-enabled" })
          );
        } else if (errorCode === "PERMISSION_DENIED") {
          message.error(
            intl.formatMessage({ id: "owner-booking-permission-denied" })
          );
        } else {
          message.error(intl.formatMessage({ id: "owner-booking-error" }));
        }
      } else if (error.response?.status === 409) {
        message.error(intl.formatMessage({ id: "owner-booking-error" }));
      } else if (error.response?.status === 400) {
        const data = error.response?.data;
        const allErrors = Object.values(data || {}).flat();
        const checkInError = [data?.check_in].flat()[0];
        const rentalIdError = [data?.rental_id].flat()[0];
        if (allErrors.includes("OWNER_BOOKING_CREATION_RESTRICTED")) {
          message.error(
            intl.formatMessage({ id: "owner-booking-creation-restricted" })
          );
        } else if (checkInError === "DATE_OVERLAP") {
          message.error(intl.formatMessage({ id: "owner-booking-overlap" }));
        } else if (checkInError === "CHECK_IN_PAST") {
          message.error(intl.formatMessage({ id: "owner-booking-past-date" }));
        } else if (checkInError === "CHECK_OUT_BEFORE_CHECK_IN") {
          message.error(
            intl.formatMessage({ id: "owner-booking-invalid-dates" })
          );
        } else if (checkInError === "CHECK_IN_BEYOND_CONTRACT_END") {
          message.error(
            intl.formatMessage({ id: "owner-booking-beyond-contract" })
          );
        } else if (rentalIdError === "RENTAL_NOT_FOUND") {
          message.error(
            intl.formatMessage({ id: "owner-booking-rental-not-found" })
          );
        } else if (rentalIdError === "RENTAL_NOT_GUESTY") {
          message.error(
            intl.formatMessage({ id: "owner-booking-rental-not-guesty" })
          );
        } else {
          message.error(
            intl.formatMessage({ id: "owner-booking-validation-error" })
          );
        }
      } else {
        message.error(intl.formatMessage({ id: "owner-booking-error" }));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      title={intl.formatMessage({ id: "owner-booking-title" })}
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="back" onClick={handleClose}>
          {intl.formatMessage({ id: "cancel" })}
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit(handleFormSubmit)}
        >
          {intl.formatMessage({ id: "owner-booking-submit" })}
        </Button>,
      ]}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div>
          <label>
            {intl.formatMessage({ id: "owner-booking-check-in" })}
            {" / "}
            {intl.formatMessage({ id: "owner-booking-check-out" })}
          </label>
          <Controller
            name="date_range"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <DatePicker.RangePicker
                {...field}
                locale={plPL}
                style={{ width: "100%" }}
                disabledDate={(current) =>
                  current &&
                  (current.isBefore(dayjs(), "day") ||
                    (contractEnd && current.isAfter(dayjs(contractEnd), "day")))
                }
                status={errors.date_range ? "error" : ""}
              />
            )}
          />
          {errors.date_range && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {intl.formatMessage({ id: "owner-booking-required" })}
            </div>
          )}
        </div>
        <Controller
          name="guests_count"
          control={control}
          defaultValue={1}
          rules={{ required: true, min: 1, max: 20 }}
          render={({ field }) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <label style={{ margin: 0 }}>
                {intl.formatMessage({ id: "owner-booking-guests" })}
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Button
                  shape="circle"
                  disabled={field.value <= 1}
                  onClick={() => field.onChange(field.value - 1)}
                >
                  −
                </Button>
                <span
                  style={{
                    minWidth: 24,
                    textAlign: "center",
                    fontSize: 16,
                  }}
                >
                  {field.value}
                </span>
                <Button
                  shape="circle"
                  disabled={field.value >= 20}
                  onClick={() => field.onChange(field.value + 1)}
                >
                  +
                </Button>
              </div>
            </div>
          )}
        />
        <div>
          <label>
            {intl.formatMessage({ id: "owner-booking-first-name" })}
          </label>
          <Controller
            name="first_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                status={errors.first_name ? "error" : ""}
                placeholder={intl.formatMessage({
                  id: "owner-booking-first-name",
                })}
              />
            )}
          />
          {errors.first_name && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {intl.formatMessage({ id: "owner-booking-required" })}
            </div>
          )}
        </div>
        <div>
          <label>{intl.formatMessage({ id: "owner-booking-last-name" })}</label>
          <Controller
            name="last_name"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                status={errors.last_name ? "error" : ""}
                placeholder={intl.formatMessage({
                  id: "owner-booking-last-name",
                })}
              />
            )}
          />
          {errors.last_name && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {intl.formatMessage({ id: "owner-booking-required" })}
            </div>
          )}
        </div>
        <div>
          <label>{intl.formatMessage({ id: "owner-booking-phone" })}</label>
          <Controller
            name="phone"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                {...field}
                status={errors.phone ? "error" : ""}
                placeholder={intl.formatMessage({
                  id: "owner-booking-phone",
                })}
              />
            )}
          />
          {errors.phone && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {intl.formatMessage({ id: "owner-booking-required" })}
            </div>
          )}
        </div>
        <div>
          <label>{intl.formatMessage({ id: "owner-booking-email" })}</label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: true,
              pattern: {
                value: EMAIL_REGEX,
                message: intl.formatMessage({
                  id: "owner-booking-email-error",
                }),
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                status={errors.email ? "error" : ""}
                placeholder={intl.formatMessage({
                  id: "owner-booking-email",
                })}
              />
            )}
          />
          {errors.email?.type === "required" && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {intl.formatMessage({ id: "owner-booking-required" })}
            </div>
          )}
          {errors.email?.type === "pattern" && (
            <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
              {intl.formatMessage({ id: "owner-booking-email-error" })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
